import { useMemo, useRef, useEffect, useState, forwardRef } from 'react'
import { DragOutlined } from '@ant-design/icons'
import {
  UiCodePricesOmnichannel,
  priceGenerationContainer,
  priceGenerationSectionId,
} from '../../../Constants/Configuration'
import Prices from './Prices'
import RenderByProfileOptionWrapper from '../../../Components/Generic/RenderByProfileOptionWrapper'
import { Row, Col, Popover, Button } from 'antd'
import MenuIcon from 'Components/Svg/MenuIcon'
import { TranslationKeys } from '../../../Constants/TranslationKeys'
import { setPriceTypeShow, setPriceFilterDateRange, setChannelType } from '../../../Slices/Kiosk/PriceSlice'
import { isProdSite, renderTranslation, stopPropagation } from '../../../Helpers/GenericHelpers'
import { PriceTypeShow, PriceDateRange } from '../../../Constants/TableConfiguration'
import { useAppDispatch, useAppSelector } from '../../../hooks'
import { CheckOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { TypeOfChannel } from 'Slices/Omnichannel/DiscountSlice'
import PricesEdit from './PricesEdit'

function PricesGridLayout({ style, className, onMouseDown, onMouseUp, onTouchEnd, children, ...props }, ref) {
  const dispatch = useAppDispatch()
  const language = useAppSelector(state => state.present.language.value)
  const articleSelected = useAppSelector(state => state.present.articleDetail.articleSelected)
  const workspacePricesVisible = useAppSelector(state => state.present.configuration.workspacePricesVisible)
  const isOmnichannelAndPricesVisibile = useAppSelector(
    state => state.present.configuration.isOmnichannelAndPricesVisibile,
  )
  const priceTypeShow = useAppSelector(state => state.present.price.priceTypeShow)
  const discountType = useAppSelector(state => state.present.price.discountType)
  const priceBoxDomElementRef = useRef(null)
  const isPriceDataFetched = articleSelected.fetchedPriceData
  const [popoverVisible, setPopoverVisible] = useState(false)
  const [filterDatesSelected, setFilterDatesSelected] = useState(null)
  useEffect(() => {
    if (isOmnichannelAndPricesVisibile) {
      const priceRef = priceBoxDomElementRef.current
      priceBoxDomElementRef && props.domElementsRefObserver && props.domElementsRefObserver.observe(priceRef)

      return () => {
        priceBoxDomElementRef && props.domElementsRefObserver && props.domElementsRefObserver.unobserve(priceRef)
      }
    }
  }, [props.domElementsRefObserver, priceBoxDomElementRef, isOmnichannelAndPricesVisibile])

  const labelSection = useMemo(() => {
    if (priceTypeShow === PriceTypeShow.omni)
      return renderTranslation(language, TranslationKeys.priceSectionTitleOmnichannel)
    if (priceTypeShow === PriceTypeShow.history)
      return renderTranslation(language, TranslationKeys.priceSectionTitleHistory)
    if (priceTypeShow === PriceTypeShow.edit) return renderTranslation(language, TranslationKeys.priceSectionTitleEdit)
    else return ''
  }, [priceTypeShow, language])

  const labelClass = useMemo(() => {
    if (priceTypeShow === PriceTypeShow.omni) return 'label-drag'
    if (priceTypeShow === PriceTypeShow.history) return 'label-drag prices-history-background'
    if (priceTypeShow === PriceTypeShow.edit) return 'label-drag prices-edit-background'
    return ''
  }, [priceTypeShow, language])

  const handleRangeSelectChange = value => {
    setFilterDatesSelected(value)
    let dateTo = dayjs().utc().valueOf()
    let dateFrom
    switch (value) {
      case PriceDateRange.month: {
        dateFrom = dayjs().subtract(1, 'month').utc().valueOf()
        break
      }
      case PriceDateRange.threeMonths: {
        dateFrom = dayjs().subtract(3, 'month').utc().valueOf()
        break
      }
      case PriceDateRange.year: {
        dateFrom = dayjs().subtract(1, 'year').utc().valueOf()
        break
      }
      case PriceDateRange.freeDates: {
        break
      }
    }
    dispatch(setPriceFilterDateRange({ dateFrom, dateTo }))
  }

  const handlingCheckboxContent = useMemo(() => {
    return (
      <Row>
        <div className={priceTypeShow === PriceTypeShow.omni ? 'price-omni-selected popover-wrap' : 'popover-wrap'}>
          <Col span={2} className='d-flex align-center'>
            {priceTypeShow === PriceTypeShow.omni ? <CheckOutlined /> : null}
          </Col>
          <Col span={22}>
            <Button
              type='text'
              onClick={() => {
                dispatch(setPriceTypeShow(PriceTypeShow.omni))
                setPopoverVisible(false)
              }}
            >
              {renderTranslation(language, TranslationKeys.priceSectionTitleOmnichannel)}
            </Button>
          </Col>
        </div>
        <div
          className={priceTypeShow === PriceTypeShow.history ? 'price-history-selected popover-wrap' : 'popover-wrap'}
        >
          <Col span={2} className='d-flex align-center'>
            {priceTypeShow === PriceTypeShow.history ? <CheckOutlined /> : null}
          </Col>
          <Col span={22}>
            <Button
              type='text'
              onClick={() => {
                dispatch(setPriceTypeShow(PriceTypeShow.history))
                handleRangeSelectChange(PriceDateRange.month)
                setPopoverVisible(false)
              }}
            >
              {renderTranslation(language, TranslationKeys.priceSectionTitleHistory)}
            </Button>
          </Col>
        </div>
        {!isProdSite() ? (
          <div className={priceTypeShow === PriceTypeShow.edit ? 'price-edit-selected popover-wrap' : 'popover-wrap'}>
            <Col span={2} className='d-flex align-center'>
              {priceTypeShow === PriceTypeShow.edit ? <CheckOutlined /> : null}
            </Col>
            <Col span={22}>
              <Button
                type='text'
                onClick={() => {
                  dispatch(setPriceTypeShow(PriceTypeShow.edit))
                  dispatch(setChannelType(TypeOfChannel.Store))
                  handleRangeSelectChange(PriceDateRange.month)
                  setPopoverVisible(false)
                }}
              >
                {renderTranslation(language, TranslationKeys.priceSectionTitleEdit)}
              </Button>
            </Col>
          </div>
        ) : null}
      </Row>
    )
  }, [discountType, dispatch, language, priceTypeShow])

  useEffect(() => {
    //essential for first render to match container width
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'))
    }, 500)
  }, [])

  return (
    <RenderByProfileOptionWrapper sectionName={UiCodePricesOmnichannel}>
      <div
        id={priceGenerationSectionId}
        grid-data={props.datagrid}
        className={className + (!isPriceDataFetched || !workspacePricesVisible ? ' d-none' : '')}
        style={{ ...style }}
        ref={ref}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        onTouchEnd={onTouchEnd}
      >
        <div className='grid-layout-adjust-wrapper'>
          <div className='drag-handle-grid-container'>
            <DragOutlined />
          </div>
          <div className='diwa-container' ref={priceBoxDomElementRef} id={priceGenerationContainer}>
            <div className={labelClass}>
              <Row className='ml-3' align={'bottom'}>
                <div className='mr-4'>
                  <Popover
                    id='pricesCheckboxContent'
                    content={handlingCheckboxContent}
                    placement='bottomLeft'
                    autoAdjustOverflow={true}
                    trigger='click'
                    open={popoverVisible}
                    onOpenChange={setPopoverVisible}
                    overlayClassName={'p-inner-w'}
                  >
                    <MenuIcon onClick={stopPropagation} className={'generic-icon menu-icon-variant'} />
                  </Popover>
                </div>
                {labelSection}
              </Row>
            </div>
            {priceTypeShow === PriceTypeShow.edit ? (
              <PricesEdit />
            ) : (
              <Prices handleRangeSelectChange={handleRangeSelectChange} filterDatesSelected={filterDatesSelected} />
            )}
          </div>
        </div>
      </div>
    </RenderByProfileOptionWrapper>
  )
}

export default forwardRef(PricesGridLayout)
